import { useEffect, useState } from 'react';
import { Edit, Save, Settings, X } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from '@/hooks';
import { useChannelsBaseUrl, useUpdateChannelsBaseUrl } from '@/hooks';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import TVChannelsLayout from '@/pages/content/TVChannelsLayout';

const TVChannelsBaseUrl = () => {
  const { t } = useTranslation();

  const [isEditingBaseUrl, setIsEditingBaseUrl] = useState(false);
  const [baseUrlError, setBaseUrlError] = useState<string | null>(null);
  const [baseUrlSuccess, setBaseUrlSuccess] = useState<string | null>(null);
  const [editingBaseUrlValue, setEditingBaseUrlValue] = useState('');

  const { data: channelsBaseUrl, isLoading: isLoadingBaseUrl, error: baseUrlLoadError } = useChannelsBaseUrl();
  const updateBaseUrlMutation = useUpdateChannelsBaseUrl();

  useEffect(() => {
    if (channelsBaseUrl) {
      setEditingBaseUrlValue(channelsBaseUrl.baseUrl || '');
    }
  }, [channelsBaseUrl]);

  useEffect(() => {
    if (baseUrlLoadError) {
      setBaseUrlError('Failed to load channels base URL');
    }
  }, [baseUrlLoadError]);

  const handleEditBaseUrl = () => {
    setIsEditingBaseUrl(true);
    setEditingBaseUrlValue(channelsBaseUrl?.baseUrl || '');
    setBaseUrlError(null);
    setBaseUrlSuccess(null);
  };

  const handleSaveBaseUrl = async () => {
    try {
      setBaseUrlError(null);
      setBaseUrlSuccess(null);

      await updateBaseUrlMutation.mutateAsync(editingBaseUrlValue);

      setIsEditingBaseUrl(false);
      setBaseUrlSuccess(t.tvChannels.baseUrl.saveSuccess);

      setTimeout(() => setBaseUrlSuccess(null), 3000);
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        setBaseUrlError(error?.response?.data?.message || t.tvChannels.baseUrl.saveError);
      } else {
        setBaseUrlError(t.tvChannels.baseUrl.saveError);
      }
    }
  };

  const handleCancelEditBaseUrl = () => {
    setIsEditingBaseUrl(false);
    setEditingBaseUrlValue(channelsBaseUrl?.baseUrl || '');
    setBaseUrlError(null);
    setBaseUrlSuccess(null);
  };

  return (
    <TVChannelsLayout>
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Settings className="h-5 w-5 text-gray-500 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">
              {t.tvChannels.baseUrl.title}
            </h3>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            {isEditingBaseUrl ? (
              <div className="flex-1 flex items-center space-x-3">
                <div className="flex-1">
                  <Input
                    ref={(baseUrlInput) => baseUrlInput?.focus()}
                    type="text"
                    value={editingBaseUrlValue}
                    onChange={(e) => setEditingBaseUrlValue(e.target.value)}
                    placeholder={t.tvChannels.baseUrl.placeholder}
                    disabled={updateBaseUrlMutation.isPending}
                  />
                </div>
                <div className="space-x-3">
                  <Button
                    onClick={handleSaveBaseUrl}
                    disabled={updateBaseUrlMutation.isPending}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-2"
                  >
                    {updateBaseUrlMutation.isPending ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {t.tvChannels.baseUrl.saving}
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <Save className="h-4 w-4 mr-1" />
                        {t.tvChannels.baseUrl.save}
                      </div>
                    )}
                  </Button>
                  <Button
                    onClick={handleCancelEditBaseUrl}
                    disabled={updateBaseUrlMutation.isPending}
                    variant="outline"
                  >
                    <X className="h-4 w-4 mr-1" />
                    {t.tvChannels.baseUrl.cancel}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-between">
                <div className="flex-1">
                  {isLoadingBaseUrl ? (
                    <div className="flex items-center text-gray-500">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 mr-2"></div>
                      {t.tvChannels.baseUrl.loading}
                    </div>
                  ) : (
                    <Input
                      type="text"
                      value={channelsBaseUrl?.baseUrl || t.tvChannels.baseUrl.notSet}
                      disabled
                    />
                  )}
                </div>
                <Button
                  onClick={handleEditBaseUrl}
                  disabled={isLoadingBaseUrl}
                  className="ml-4 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2"
                >
                  <Edit className="h-4 w-4 mr-1" />
                  {t.tvChannels.baseUrl.edit}
                </Button>
              </div>
            )}
          </div>

          {baseUrlSuccess && (
            <div className="rounded-md bg-green-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">{baseUrlSuccess}</p>
                </div>
              </div>
            </div>
          )}

          {baseUrlError && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{baseUrlError}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </TVChannelsLayout>
  );
};

export default TVChannelsBaseUrl;
